/**
 * Service d'extraction AI pour les notes GEDCOM
 * Utilise le Worker Cloudflare AI pour extraire automatiquement les événements
 * V8: Support traitement groupé par individu avec contexte chronologique
 */

import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import { logger } from '../utils/debug/logger.js';
import { AI_NOTES_ENDPOINT, aiNotesHeaders } from './aiNotesEndpoint.js';

class AINotesService {
  constructor() {
    this.isProcessing = false;
    this.cache = new Map();
    this.processingQueue = [];
  }

  /**
   * Traite toutes les notes d'une personne
   * @param {string} personId - ID de la personne
   * @param {Map} notesCache - Cache des notes depuis gedcomDataStore
   * @returns {Promise<Object>} Événements extraits
   */
  async extractEventsForPerson(personId, notesCache) {
    try {
      // Récupérer toutes les notes liées à cette personne
      const personNotes = this.getPersonNotes(personId, notesCache);
      
      if (!personNotes || personNotes.length === 0) {
        logger.ai.debug(`Aucune note trouvée pour ${personId}`);
        return { success: true, events: [] };
      }

      logger.ai.info(`🤖 Extraction AI pour ${personId}: ${personNotes.length} notes`);

      // Vérifier le cache local
      const cacheKey = this.generateCacheKey(personNotes);
      if (this.cache.has(cacheKey)) {
        logger.ai.debug(`Cache hit pour ${personId}`);
        return this.cache.get(cacheKey);
      }

      // Appeler le Worker AI
      const result = await this.callAIWorker(personNotes);
      
      if (result.success) {
        // Mettre en cache
        this.cache.set(cacheKey, result);
        
        // Émettre événement pour mise à jour UI
        storeEvents.emit(EVENTS.AI_NOTES.EXTRACTION_COMPLETED, {
          personId,
          eventsCount: result.data?.summary?.total_events_extracted || 0,
          processingTime: result.data?.processing_time_ms
        });

        logger.ai.success(`✅ Extraction réussie pour ${personId}: ${result.data?.summary?.total_events_extracted || 0} événements`);
      } else {
        logger.ai.error(`❌ Échec extraction pour ${personId}:`, result.error);
        
        storeEvents.emit(EVENTS.AI_NOTES.EXTRACTION_FAILED, {
          personId,
          error: result.error
        });
      }

      return result;

    } catch (error) {
      logger.ai.error(`Erreur lors de l'extraction AI pour ${personId}:`, error);
      
      storeEvents.emit(EVENTS.AI_NOTES.EXTRACTION_FAILED, {
        personId,
        error: error.message
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Traite un batch de notes via le Worker AI
   * @param {Array} notes - Liste des notes à traiter
   * @returns {Promise<Object>} Résultat de l'extraction
   */
  async callAIWorker(notes) {
    const payload = {
      notes: notes.map(note => ({
        id: note.id,
        text: note.text
      })),
      lang: 'fr',
      useCache: true
    };

    const response = await fetch(AI_NOTES_ENDPOINT, {
      method: 'POST',
      headers: aiNotesHeaders(),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Worker AI error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Récupère les notes associées à une personne
   * @param {string} personId - ID de la personne
   * @param {Map} notesCache - Cache des notes
   * @returns {Array} Liste des notes
   */
  getPersonNotes(personId, notesCache) {
    if (!notesCache || !notesCache.size) return [];

    const personNotes = [];
    
    // Parcourir le cache des notes pour trouver celles liées à cette personne
    for (const [noteId, noteData] of notesCache.entries()) {
      if (noteData && noteData.text) {
        // Vérifier si la note mentionne cette personne (heuristique simple)
        if (noteData.linkedPersons?.includes(personId) || 
            noteData.text.includes(personId)) {
          personNotes.push({
            id: noteId,
            text: noteData.text,
            linkedPersons: noteData.linkedPersons || []
          });
        }
      }
    }

    return personNotes;
  }

  /**
   * Traite toutes les notes du GEDCOM en arrière-plan
   * @param {Map} notesCache - Cache complet des notes
   * @param {Map} individualsCache - Cache des individus
   * @returns {Promise<Object>} Résultats globaux
   */
  async processAllNotes(notesCache, individualsCache) {
    if (this.isProcessing) {
      logger.ai.warn('Traitement AI déjà en cours');
      return { success: false, error: 'Processing already in progress' };
    }

    this.isProcessing = true;
    const startTime = Date.now();

    try {
      logger.ai.info(`🚀 Début traitement AI global: ${notesCache.size} notes`);
      
      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_STARTED, {
        totalNotes: notesCache.size
      });

      // Convertir toutes les notes en format Worker
      // Exclure les notes attachées à des événements (préfixe e_) car elles sont déjà
      // capturées comme événements structurés avec date/lieu dans le GEDCOM
      const allNotes = Array.from(notesCache.entries())
        .filter(([id]) => !id.startsWith('e_'))
        .map(([id, noteData]) => ({
          id,
          text: noteData.text || ''
        }))
        .filter(note => note.text.trim().length > 10); // Filtrer les notes trop courtes

      if (allNotes.length === 0) {
        logger.ai.warn('Aucune note suffisamment longue pour traitement AI');
        return { success: true, events: [] };
      }

      // Traitement par chunks pour éviter les timeouts
      const CHUNK_SIZE = 20;
      const chunks = [];
      
      for (let i = 0; i < allNotes.length; i += CHUNK_SIZE) {
        chunks.push(allNotes.slice(i, i + CHUNK_SIZE));
      }

      const allResults = [];
      let processedCount = 0;

      for (const [index, chunk] of chunks.entries()) {
        logger.ai.debug(`Traitement chunk ${index + 1}/${chunks.length} (${chunk.length} notes)`);
        
        try {
          const result = await this.callAIWorker(chunk);
          
          if (result.success) {
            allResults.push(result);
            processedCount += chunk.length;
            
            // Mettre à jour le progrès
            storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_PROGRESS, {
              processed: processedCount,
              total: allNotes.length,
              percentage: Math.round((processedCount / allNotes.length) * 100)
            });
          }
          
          // Courte pause entre les chunks
          if (index < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          
        } catch (error) {
          logger.ai.error(`Erreur chunk ${index + 1}:`, error);
          // Continue avec le chunk suivant
        }
      }

      // Consolider les résultats
      const consolidatedResult = this.consolidateResults(allResults);
      
      const processingTime = Date.now() - startTime;
      logger.ai.success(`✅ Traitement AI terminé en ${processingTime}ms`);
      
      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_COMPLETED, {
        ...consolidatedResult.summary,
        processingTime
      });

      return consolidatedResult;

    } catch (error) {
      logger.ai.error('Erreur lors du traitement AI global:', error);
      
      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_FAILED, {
        error: error.message
      });
      
      return {
        success: false,
        error: error.message
      };
    } finally {
      this.isProcessing = false;
    }
  }

  // ---------------------------------------------------------------------------
  // V8: Traitement groupé par individu avec contexte chronologique
  // ---------------------------------------------------------------------------

  /**
   * Traite les notes groupées par individu avec leur contexte chronologique.
   * Point d'entrée principal pour le nouveau flux contextuel.
   *
   * @param {Map<string, { context: Object, notes: Array<{id, text}> }>} groupedByIndividual
   * @returns {Promise<Object>} Résultat avec eventsByIndividual
   */
  async processAllGrouped(groupedByIndividual, useCache = true) {
    if (this.isProcessing) {
      logger.ai.warn('Traitement AI déjà en cours');
      return { success: false, error: 'Processing already in progress' };
    }

    this.isProcessing = true;
    const startTime = Date.now();

    try {
      const entries = Array.from(groupedByIndividual.entries());
      const totalIndividuals = entries.length;
      const totalNotes = entries.reduce((sum, [, data]) => sum + data.notes.length, 0);

      // Log supprimé : info dupliquée dans aiNotesIntegration.js

      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_STARTED, {
        totalNotes,
        totalIndividuals
      });

      // Chunker par 5 individus par requête worker
      const CHUNK_SIZE = 5;
      const chunks = [];
      for (let i = 0; i < entries.length; i += CHUNK_SIZE) {
        chunks.push(entries.slice(i, i + CHUNK_SIZE));
      }

      const allResults = [];
      let processedIndividuals = 0;
      let failedIndividuals = 0;

      for (const [index, chunk] of chunks.entries()) {
        logger.ai.debug(`Traitement chunk ${index + 1}/${chunks.length} (${chunk.length} individus)`);

        try {
          const payload = {
            individuals: chunk.map(([id, data]) => ({
              id,
              context: data.context,
              notes: data.notes
            })),
            lang: 'fr',
            useCache: useCache
          };

          const result = await this.callAIWorkerRaw(payload);

          if (result.success) {
            allResults.push(result);
          } else {
            failedIndividuals += chunk.length;
            logger.ai.warn(`Chunk ${index + 1}: échec worker (success=false)`);
          }
        } catch (error) {
          failedIndividuals += chunk.length;
          logger.ai.error(`Erreur chunk ${index + 1}:`, error);
        }

        // Progression émise pour chaque chunk, y compris en échec,
        // avec le décompte des échecs pour un affichage honnête
        processedIndividuals += chunk.length;
        storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_PROGRESS, {
          processed: processedIndividuals,
          failed: failedIndividuals,
          total: totalIndividuals,
          percentage: Math.round((processedIndividuals / totalIndividuals) * 100)
        });

        // Courte pause entre les chunks
        if (index < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      const consolidatedResult = this.consolidateGroupedResults(allResults);

      const processingTime = Date.now() - startTime;
      logger.ai.success(`✅ Traitement AI groupé terminé en ${processingTime}ms`);

      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_COMPLETED, {
        ...consolidatedResult.summary,
        processingTime
      });

      return consolidatedResult;

    } catch (error) {
      logger.ai.error('Erreur lors du traitement AI groupé:', error);

      storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_FAILED, {
        error: error.message
      });

      return { success: false, error: error.message };
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Envoie un payload brut au Worker AI (supporte les deux formats).
   * @param {Object} payload - Payload complet à envoyer
   * @returns {Promise<Object>} Résultat du Worker
   */
  async callAIWorkerRaw(payload) {
    const response = await fetch(AI_NOTES_ENDPOINT, {
      method: 'POST',
      headers: aiNotesHeaders(),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Worker AI error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Consolide les résultats du format groupé par individu.
   * Produit un objet eventsByIndividual keyed par individualId.
   *
   * @param {Array} results - Résultats bruts des appels Worker
   * @returns {Object} Résultat consolidé
   */
  consolidateGroupedResults(results) {
    const eventsByIndividual = {};
    let totalEventsExtracted = 0;
    let totalSuccessful = 0;
    let totalFailed = 0;

    for (const result of results) {
      if (!result.success || !result.data?.results) continue;

      for (const individualResult of result.data.results) {
        if (individualResult.success && individualResult.data?.events) {
          const events = individualResult.data.events.map(event => ({
            ...event,
            source_note_ids: individualResult.noteIds || [],
            extraction_method: 'ai_worker_v8'
          }));

          const id = individualResult.individualId;
          if (!eventsByIndividual[id]) {
            eventsByIndividual[id] = [];
          }
          eventsByIndividual[id].push(...events);
          totalEventsExtracted += events.length;
          totalSuccessful++;
        } else {
          totalFailed++;
        }
      }
    }

    return {
      success: true,
      eventsByIndividual,
      summary: {
        total_events_extracted: totalEventsExtracted,
        total_individuals_processed: totalSuccessful + totalFailed,
        successful_extractions: totalSuccessful,
        failed_extractions: totalFailed,
        success_rate: (totalSuccessful + totalFailed) > 0
          ? totalSuccessful / (totalSuccessful + totalFailed)
          : 0
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Reconciliation: compare extracted events vs existing events via AI
  // ---------------------------------------------------------------------------

  /**
   * Reconcile newly extracted events against existing events for an individual.
   * Uses AI to classify each new event as new/enrich/duplicate.
   *
   * @param {Array} existingEvents - Simplified existing events (decompressed, with index)
   * @param {Array} newEvents - AI-extracted events from Wikipedia
   * @param {Object} context - Minimal context (name, surname, birthYear, deathYear)
   * @returns {Promise<Array>} Reconciled array: [{index, action, match_index, reasoning, merged_fields?}]
   */
  async reconcileEvents(existingEvents, newEvents, context) {
    try {
      const payload = {
        mode: 'reconcile',
        existingEvents,
        newEvents,
        context
      };

      logger.ai.info(`🔄 Reconciliation: ${newEvents.length} new vs ${existingEvents.length} existing events`);

      const result = await this.callAIWorkerRaw(payload);

      if (result.success && result.data?.reconciled) {
        const reconciled = result.data.reconciled;
        const stats = {
          new: reconciled.filter(r => r.action === 'new').length,
          enrich: reconciled.filter(r => r.action === 'enrich').length,
          duplicate: reconciled.filter(r => r.action === 'duplicate').length,
          fallback: result.data.fallback || false
        };
        logger.ai.info(`✅ Reconciliation: ${stats.new} new, ${stats.enrich} enrich, ${stats.duplicate} duplicate${stats.fallback ? ' (fallback)' : ''}`);
        return reconciled;
      }

      // Fallback: treat all as new if reconciliation fails
      logger.ai.warn('Reconciliation failed, defaulting to all-new');
      return newEvents.map((_, i) => ({
        index: i,
        action: 'new',
        match_index: -1,
        reasoning: 'Reconciliation unavailable'
      }));

    } catch (error) {
      logger.ai.error('Reconciliation error:', error);
      // Graceful degradation: all events treated as new
      return newEvents.map((_, i) => ({
        index: i,
        action: 'new',
        match_index: -1,
        reasoning: 'Reconciliation error — defaulting to new'
      }));
    }
  }

  // ---------------------------------------------------------------------------
  // Legacy: traitement non-groupé (conservé pour backward compat)
  // ---------------------------------------------------------------------------

  /**
   * Consolide les résultats de plusieurs appels API (format legacy)
   * @param {Array} results - Liste des résultats
   * @returns {Object} Résultat consolidé
   */
  consolidateResults(results) {
    const allEvents = [];
    let totalProcessed = 0;
    let totalSuccessful = 0;
    let totalFailed = 0;

    for (const result of results) {
      if (result.success && result.data) {
        totalProcessed += result.data.total_notes || 0;
        totalSuccessful += result.data.successful_extractions || 0;
        totalFailed += result.data.failed_extractions || 0;

        // Collecter tous les événements extraits
        if (result.data.results) {
          for (const noteResult of result.data.results) {
            if (noteResult.success && noteResult.data?.events) {
              allEvents.push(...noteResult.data.events.map(event => ({
                ...event,
                source_note_id: noteResult.noteId,
                extraction_method: 'ai_worker'
              })));
            }
          }
        }
      }
    }

    return {
      success: true,
      events: allEvents,
      summary: {
        total_notes_processed: totalProcessed,
        successful_extractions: totalSuccessful,
        failed_extractions: totalFailed,
        total_events_extracted: allEvents.length,
        success_rate: totalProcessed > 0 ? (totalSuccessful / totalProcessed) : 0
      }
    };
  }

  /**
   * Génère une clé de cache basée sur le contenu des notes
   * @param {Array} notes - Liste des notes
   * @returns {string} Clé de cache
   */
  generateCacheKey(notes) {
    const content = notes.map(n => n.text).join('|');
    return `ai_notes_${this.simpleHash(content)}`;
  }

  /**
   * Hash simple pour les clés de cache
   * @param {string} str - Chaîne à hasher
   * @returns {string} Hash
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convertir en 32bit integer
    }
    return hash.toString(16);
  }

  /**
   * Nettoie le cache local
   */
  clearCache() {
    this.cache.clear();
    logger.ai.debug('Cache AI nettoyé');
  }

  /**
   * Retourne les statistiques du service
   * @returns {Object} Statistiques
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      isProcessing: this.isProcessing,
      queueLength: this.processingQueue.length,
      endpoint: AI_NOTES_ENDPOINT
    };
  }
}

// Instance singleton
const aiNotesService = new AINotesService();

export default aiNotesService;